import { useSettings } from "../contexts/SettingsContext.js";
import type { PriceBreakdown } from "../lib/types.js";
import { formatCzk, formatEur } from "../lib/format.js";

/**
 * PriceDisplay — respektuje uživatelské nastavení DPH režimu a měny.
 *
 * Zobrazí primární cenu (podle settings.vatMode a settings.currency)
 * a pod ní doplňkovou info (druhá měna, případně DPH údaj).
 */

export function PriceDisplay({
  price,
  size = "md",
}: {
  price: PriceBreakdown;
  size?: "sm" | "md" | "lg";
}): JSX.Element {
  const { vatMode, currency } = useSettings();

  const netPrimary =
    currency === "CZK" ? formatCzk(price.net.czk) : formatEur(price.net.eur);
  const grossPrimary =
    currency === "CZK" ? formatCzk(price.gross.czk) : formatEur(price.gross.eur);
  const netSecondary =
    currency === "CZK" ? formatEur(price.net.eur) : formatCzk(price.net.czk);
  const grossSecondary =
    currency === "CZK" ? formatEur(price.gross.eur) : formatCzk(price.gross.czk);

  const sizes = {
    sm: { primary: "text-sm font-semibold", secondary: "text-xs text-zinc-500" },
    md: { primary: "text-base font-semibold", secondary: "text-xs text-zinc-500" },
    lg: { primary: "text-xl font-semibold", secondary: "text-sm text-zinc-500" },
  } as const;

  if (vatMode === "net") {
    return (
      <div className="flex flex-col">
        <span className={sizes[size].primary}>{netPrimary}</span>
        <span className={sizes[size].secondary}>
          bez DPH · {netSecondary}
        </span>
      </div>
    );
  }
  if (vatMode === "gross") {
    return (
      <div className="flex flex-col">
        <span className={sizes[size].primary}>{grossPrimary}</span>
        <span className={sizes[size].secondary}>
          s DPH · {grossSecondary}
        </span>
      </div>
    );
  }
  // both
  return (
    <div className="flex flex-col">
      <span className={sizes[size].primary}>{grossPrimary}</span>
      <span className={sizes[size].secondary}>
        s DPH · <span className="text-zinc-400">{netPrimary} bez DPH</span>
      </span>
      {size === "lg" && (
        <span className="text-xs text-zinc-400 mt-0.5">
          {grossSecondary} · DPH {price.vatRate}%
        </span>
      )}
    </div>
  );
}

/**
 * VatToggle — sdílený přepínač DPH režimu v hlavičce.
 */
export function VatToggle(): JSX.Element {
  const { vatMode, setVatMode } = useSettings();
  const options: Array<{ value: typeof vatMode; label: string }> = [
    { value: "gross", label: "s DPH" },
    { value: "net", label: "bez DPH" },
    { value: "both", label: "Obojí" },
  ];
  return (
    <div
      role="group"
      aria-label="Režim zobrazení DPH"
      className="inline-flex rounded-lg bg-white/10 p-0.5 text-xs"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => setVatMode(opt.value)}
          className={`px-2.5 min-h-11 sm:min-h-0 py-1 rounded-md transition-colors ${
            vatMode === opt.value
              ? "bg-white text-zinc-900 font-medium"
              : "text-zinc-300 hover:text-white"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function CurrencyToggle(): JSX.Element {
  const { currency, setCurrency } = useSettings();
  const options = ["CZK", "EUR"] as const;
  return (
    <div
      role="group"
      aria-label="Měna"
      className="inline-flex rounded-lg bg-white/10 p-0.5 text-xs"
    >
      {options.map((c) => (
        <button
          key={c}
          onClick={() => setCurrency(c)}
          className={`px-2.5 min-h-11 sm:min-h-0 py-1 rounded-md transition-colors ${
            currency === c
              ? "bg-white text-zinc-900 font-medium"
              : "text-zinc-300 hover:text-white"
          }`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}
